import { PERMISSIONS, type Permission, type Role } from '@/config/constants'

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: Role, permission: Permission): boolean {
  const allowedRoles = PERMISSIONS[permission] as readonly string[]
  return allowedRoles.includes(role)
}

/**
 * Check if a role has any of the specified permissions
 */
export function hasAnyPermission(role: Role, permissions: Permission[]): boolean {
  return permissions.some((permission) => hasPermission(role, permission))
}

/**
 * Check if a role has all of the specified permissions
 */
export function hasAllPermissions(role: Role, permissions: Permission[]): boolean {
  return permissions.every((permission) => hasPermission(role, permission))
}

/**
 * Get all permissions for a role
 */
export function getRolePermissions(role: Role): Permission[] {
  return (Object.entries(PERMISSIONS) as [Permission, readonly string[]][])
    .filter(([, roles]) => roles.includes(role))
    .map(([permission]) => permission)
}

/**
 * Check if user can manage other users (owner or super_admin)
 */
export function canManageUsers(role: Role): boolean {
  return hasPermission(role, 'USERS_MANAGE')
}

/**
 * Check if user can manage connectors (owner or super_admin)
 */
export function canManageConnectors(role: Role): boolean {
  return hasPermission(role, 'CONNECTORS_MANAGE')
}

/**
 * Check if user can export data (analyst, owner, super_admin)
 */
export function canExportData(role: Role): boolean {
  return hasPermission(role, 'ANALYTICS_EXPORT')
}

/**
 * Check if user can manage tenant settings (owner or super_admin)
 */
export function canManageSettings(role: Role): boolean {
  return hasPermission(role, 'SETTINGS_MANAGE')
}

/**
 * Check if user can manage jobs (owner or super_admin)
 */
export function canManageJobs(role: Role): boolean {
  return hasPermission(role, 'JOBS_MANAGE')
}
